import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

// Guards
import { AuthGuard } from './guards';
// Layout Components
import { DefaultLayoutComponent } from './layouts/default/default.component';
import { LandingSiteLayoutComponent } from './layouts/landing-site/landing-site.component';
import { OutsideTheGateLayoutComponent } from './layouts/outside-the-gate/outside-the-gate.component';
import { AnalyticsHomeComponent } from './pages/analytics/analytics-home/analytics-home.component';
// Page Components
import { AuthtokenComponent } from './pages/auth/authtoken/authtoken.component';
import { LoginComponent } from './pages/auth/login/login.component';
import { ResetPasswordComponent } from './pages/auth/reset-password/reset-password.component';
import { CustomersEditComponent } from './pages/customers/customers-edit/customers-edit.component';
import { CustomersHomeComponent } from './pages/customers/customers-home/customers-home.component';
import { DashboardFboComponent } from './pages/dashboards/dashboard-fbo/dashboard-fbo.component';
import { DashboardFboUpdatedComponent } from './pages/dashboards/dashboard-fbo-updated/dashboard-fbo-updated.component';
import { DashboardHomeComponent } from './pages/dashboards/dashboard-home/dashboard-home.component';
import { EmailTemplatesEditComponent } from './pages/email-templates/email-templates-edit/email-templates-edit.component';
import { EmailTemplatesHomeComponent } from './pages/email-templates/email-templates-home/email-templates-home.component';
import { FboPricesHomeComponent } from './pages/fbo-prices/fbo-prices-home/fbo-prices-home.component';
import { FbosEditComponent } from './pages/fbos/fbos-edit/fbos-edit.component';
import { FbosHomeComponent } from './pages/fbos/fbos-home/fbos-home.component';
import { FlightWatchComponent } from './pages/flight-watch/flight-watch.component';
import { FuelreqsHomeComponent } from './pages/fuelreqs/fuelreqs-home/fuelreqs-home.component';
import { GroupAnalyticsHomeComponent } from './pages/group-analytics/group-analytics-home/group-analytics-home.component';
import { GroupCustomersHomeComponent } from './pages/group-customers/group-customers-home/group-customers-home.component';
import { GroupsEditComponent } from './pages/groups/groups-edit/groups-edit.component';
import { GroupsHomeComponent } from './pages/groups/groups-home/groups-home.component';
import { PricingTemplatesEditComponent } from './pages/pricing-templates/pricing-templates-edit/pricing-templates-edit.component';
import { PricingTemplatesHomeComponent } from './pages/pricing-templates/pricing-templates-home/pricing-templates-home.component';
import { UsersEditComponent } from './pages/users/users-edit/users-edit.component';
import { UsersHomeComponent } from './pages/users/users-home/users-home.component';
import { FboGeofencingHomeComponent } from './pages/fbo-geofencing/fbo-geofencing-home/fbo-geofencing-home.component';
import { AntennaStatusHomeComponent } from './pages/antenna-status/antenna-status-home/antenna-status-home.component';
import { MapAllowedIcaosHomeComponent } from './pages/map-allowed-icaos/map-allowed-icaos-home/map-allowed-icaos-home.component';
import { LobbyViewComponent } from './pages/lobby-view/lobby-view.component';
import { PublicViewComponent } from './layouts/public-view/public-view.component';
import { AboutFbolinxComponent } from './pages/about-fbolinx/about-fbolinx.component';
import { ServiceOrdersHomeComponent } from './pages/service-orders/service-orders-home/service-orders-home.component';
import { ServicesAndFeesHomeComponent } from './pages/services-and-fees-home/services-and-fees-home.component';
import { ForbiddenComponent } from './pages/forbidden/forbidden.component';
import { UserRole } from './enums/user-role';
import { hasFboSelectedAndHasRole } from './guards/roles.guard';

const defaultRoutes: Routes = [
    {
        canActivate: [AuthGuard],
        component: GroupsHomeComponent,
        data: {
            expectedRoles: [UserRole.Conductor],
        },
        path: 'groups',
    },
    {
        canActivate: [AuthGuard],
        component: GroupsEditComponent,
        data: {
            expectedRoles: [UserRole.Conductor],
        },
        path: 'groups/:id',
    },
    {
        canActivate: [AuthGuard],
        component: FbosHomeComponent,
        data: {
            expectedRoles: [UserRole.GroupAdmin, UserRole.Conductor],
        },
        path: 'fbos',
    },
    {
        canActivate: [AuthGuard],
        component: FbosEditComponent,
        data: {
            expectedRoles: [UserRole.GroupAdmin, UserRole.Conductor],
        },
        path: 'fbos/:id',
    },
    {
        canActivate: [AuthGuard],
        component: FboGeofencingHomeComponent,
        data: {
            expectedRoles: [UserRole.Conductor],
        },
        path: 'fbo-geofencing',
    },
    {
        canActivate: [AuthGuard],
        component: AntennaStatusHomeComponent,
        data: {
            expectedRoles: [UserRole.Conductor],
        },
        path: 'antenna-status',
    },
    {
        canActivate: [AuthGuard],
        component: MapAllowedIcaosHomeComponent,
        data: {
            expectedRoles: [UserRole.Conductor],
        },
        path: 'map-allowed-icaos',
    },
    {
        canActivate: [AuthGuard],
        component: GroupAnalyticsHomeComponent,
        data: {
            expectedRoles: [UserRole.GroupAdmin, UserRole.Conductor],
        },
        path: 'group-analytics',
    },
    {
        canActivate: [AuthGuard],
        component: GroupCustomersHomeComponent,
        data: {
            expectedRoles: [UserRole.GroupAdmin, UserRole.Conductor],
        },
        path: 'group-customers',
    },
    {
        canMatch: [hasFboSelectedAndHasRole],
        canActivate: [AuthGuard],
        component: CustomersHomeComponent,
        data: {
            expectedRoles: [UserRole.All],
        },
        path: 'customers',
    },
    {
        canMatch: [hasFboSelectedAndHasRole],
        canActivate: [AuthGuard],
        component: CustomersEditComponent,
        data: {
            expectedRoles: [UserRole.Primary,UserRole.GroupAdmin,UserRole.Conductor,UserRole.Member,UserRole.CSR],
        },
        path: 'customers/:id',
    },
    {
        canMatch: [hasFboSelectedAndHasRole],
        canActivate: [AuthGuard],
        component: DashboardHomeComponent,
        data: {
            expectedRoles: [UserRole.All],
        },
        path: 'dashboard',
    },
    {
        canMatch: [hasFboSelectedAndHasRole],
        canActivate: [AuthGuard],
        component: DashboardFboComponent,
        data: {
            expectedRoles: [UserRole.All],
        },
        path: 'dashboard-fbo',
    },
    {
        canMatch: [hasFboSelectedAndHasRole],
        canActivate: [AuthGuard],
        component: DashboardFboUpdatedComponent,
        data: {
            expectedRoles: [UserRole.All],
        },
        path: 'dashboard-fbo-updated',
    },
    {
        canMatch: [hasFboSelectedAndHasRole],
        canActivate: [AuthGuard],
        component: DashboardFboUpdatedComponent,
        data: {
            expectedRoles: [UserRole.All],
        },
        path: 'dashboard-csr',
    },
    {
        canMatch: [hasFboSelectedAndHasRole],
        canActivate: [AuthGuard],
        component: FboPricesHomeComponent,
        data: {
            expectedRoles: [UserRole.All],
        },
        path: 'fbo-prices',
    },
    {
        canMatch: [hasFboSelectedAndHasRole], 
        canActivate: [AuthGuard],
        component: FuelreqsHomeComponent,
        data: {
            expectedRoles: [UserRole.All],
        },
        path: 'fuelreqs',
    },
    {
        canMatch: [hasFboSelectedAndHasRole], 
        canActivate: [AuthGuard],
        component: PricingTemplatesHomeComponent,
        data: {
            expectedRoles: [UserRole.All],
        },
        path: 'pricing-templates',
    },
    {
        canMatch: [hasFboSelectedAndHasRole],
        canActivate: [AuthGuard],
        component: PricingTemplatesEditComponent,
        data: {
            expectedRoles: [UserRole.All],
        },
        path: 'pricing-templates/:id',
    },
    {
        canMatch: [hasFboSelectedAndHasRole],
        canActivate: [AuthGuard],
        component: EmailTemplatesHomeComponent,
        data: {
            expectedRoles: [UserRole.All],
        },
        path: 'email-templates',
    },
    {
        canMatch: [hasFboSelectedAndHasRole],
        canActivate: [AuthGuard],
        data: {
            expectedRoles: [UserRole.All],
        },
        component: EmailTemplatesEditComponent,
        path: 'email-templates/:id',
    },
    {
        canMatch: [hasFboSelectedAndHasRole],
        canActivate: [AuthGuard],
        data: {
            expectedRoles: [UserRole.All],
        },
        component: ServicesAndFeesHomeComponent,
        path: 'services-and-fees',
    },
    {
        canMatch: [hasFboSelectedAndHasRole],
        canActivate: [AuthGuard],
        component: AnalyticsHomeComponent,
        data: {
            expectedRoles: [UserRole.Primary, UserRole.GroupAdmin, UserRole.Conductor, UserRole.AnalysisOnly],
        },
        path: 'analytics',
    },
    {
        canMatch: [hasFboSelectedAndHasRole],
        canActivate: [AuthGuard],
        component: UsersHomeComponent,
        data: {
            expectedRoles: [UserRole.Primary, UserRole.GroupAdmin, UserRole.Conductor],
        },
        path: 'users',
    },
    {
        canMatch: [hasFboSelectedAndHasRole],
        canActivate: [AuthGuard],
        component: UsersEditComponent,
        data: {
            expectedRoles: [UserRole.Primary, UserRole.GroupAdmin, UserRole.Conductor],
        },
        path: 'users/:id',
    },
    {
        canMatch: [hasFboSelectedAndHasRole],
        canActivate: [AuthGuard],
        data: {
            expectedRoles: [UserRole.All],
        },
        component: FlightWatchComponent,
        path: 'flight-watch',
    },
    {
        canMatch: [hasFboSelectedAndHasRole],
        canActivate: [AuthGuard],
        data: {
            expectedRoles: [UserRole.All],
        },
        component: ServiceOrdersHomeComponent,
        path: 'service-orders',
    },

    {
        canActivate: [AuthGuard],
        component: AboutFbolinxComponent,
        data: {
            expectedRoles: [UserRole.NonRev],
        },
        path: 'about-fbolinx',
    },
];

const outsideTheGateRoutes: Routes = [
    {
        component: AuthtokenComponent,
        path: 'auth',
    },
];

const publicViewRoutes: Routes = [
    {
        canActivate: [AuthGuard],
        component: LobbyViewComponent,
        path: 'lobby-view',
    },
];

const routes: Routes = [
    {
        component: LandingSiteLayoutComponent,
        path: '',
        //children: landingSiteRoutes,
    },
    {
        children: defaultRoutes,
        component: DefaultLayoutComponent,
        path: 'default-layout',
    },
    {
        children: publicViewRoutes,
        component: PublicViewComponent,
        path: 'public-layout',
    },
    {
        children: outsideTheGateRoutes,
        component: OutsideTheGateLayoutComponent,
        path: 'outside-the-gate-layout',
    },
    {
        component: LoginComponent,
        path: 'app-login',
    },
    {
        component: ResetPasswordComponent,
        path: 'reset-password',
    },
    {
        component: ForbiddenComponent,
        path: 'forbidden',
    },
    {
        component: LandingSiteLayoutComponent,
        path: '**',
        //children: landingSiteRoutes,
    },
];

@NgModule({
    exports: [RouterModule],
    imports: [
        RouterModule.forRoot(routes, {}),
    ],
})
export class AppRoutingModule {}
